import { useEffect, useState, useCallback, useMemo } from "react";
import { Col, Form, Select } from "antd";
import debounce from "lodash.debounce";
import {
  EXISTING_PO_MAP_LIST,
  GET_STYLE_LIST_ENDPOINT,
  PO_MAP_NEW_ENDPOINT,
  SUBSTYLE_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import {
  isArrayAndHasValue,
  renderSubstyleName,
} from "../../../utils/functions";

const FoldingRequirementFilterFields = ({
  onChangeSubStyle,
  style_id_watch,
  view,
}) => {
  const [poList, setPoList] = useState([]);
  const [styleList, setStyleList] = useState([]);
  const [subStyleList, setSubStyleList] = useState([]);

  const { Option } = Select;

  // Fetch data with error handling
  const fetchData = async (endpoint, params = {}) => {
    try {
      const response = await getData(endpoint, false, params);
      if (response?.data?.code === 200) {
        return response?.data?.data?.data || [];
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    return [];
  };

  // Fetch style list
  const getStyleList = useCallback(
    debounce(async (filterValues = {}) => {
      const data = await fetchData(GET_STYLE_LIST_ENDPOINT, {
        per_page: 10,
        ...filterValues,
      });
      setStyleList(data);
    }, 300),
    [],
  );

  // Fetch sub-style list
  const getSubStyles = useCallback(
    debounce(async (filterValues = {}) => {
      const data = await fetchData(SUBSTYLE_LIST, {
        per_page: 10,
        page: 1,
        ...filterValues,
      });
      setSubStyleList(data);
    }, 300),
    [],
  );

  // Fetch PO list
  const getPOList = useCallback(
    debounce(async (filterValues = {}) => {
      const data = await fetchData(PO_MAP_NEW_ENDPOINT, {
        per_page: 10,
        page: 1,
        ...filterValues,
      });
      setPoList(data);
    }, 300),
    [],
  );

  // Initial data fetch
  useEffect(() => {
    getStyleList();
    getSubStyles();
    getPOList();
  }, [getStyleList, getSubStyles, getPOList]);

  // Memoized options for better performance
  const styleOptions = useMemo(
    () =>
      isArrayAndHasValue(styleList) &&
      styleList.map((style) => (
        <Option key={style.id} value={style.id}>
          {style.style_no}
        </Option>
      )),
    [styleList],
  );

  const subStyleOptions = useMemo(
    () =>
      isArrayAndHasValue(subStyleList) &&
      subStyleList.map((item) => (
        <Option key={item.id} value={item.id} label={item.sub_style_no}>
          {renderSubstyleName(item)}
        </Option>
      )),
    [subStyleList],
  );

  const poOptions = useMemo(
    () =>
      isArrayAndHasValue(poList) &&
      poList.map((po) => (
        <Option key={po.id} value={po.id}>
          {po.original_po}
        </Option>
      )),
    [poList],
  );

  return (
    <>
      <Col span={4}>
        <Form.Item label="Style No." name="style_id">
          <Select
            showSearch
            placeholder="Select a Style"
            optionFilterProp="children"
            size="small"
            allowClear
            onClear={() => getStyleList()}
            onSearch={(value) => getStyleList({ style_no: value })}
            filterOption={false}
          >
            {styleOptions}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Sub Style" name="sub_style_id">
          <Select
            showSearch
            placeholder="Search to Select"
            optionFilterProp="children"
            size="small"
            disabled={view}
            allowClear
            onClear={() => getSubStyles()}
            onSearch={(value) =>
              getSubStyles({
                sub_style_no: value,
                style_id: style_id_watch || null,
              })
            }
            onSelect={(value) => onChangeSubStyle && onChangeSubStyle(value)}
            filterOption={false}
          >
            {subStyleOptions}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Original PO" name="po_map_id">
          <Select
            showSearch
            allowClear
            onClear={() => getPOList()}
            placeholder="Search to Select"
            optionFilterProp="children"
            size="small"
            onSearch={(value) =>
              getPOList({
                original_po: value,
              })
            }
            filterOption={false}
          >
            {poOptions}
          </Select>
        </Form.Item>
      </Col>
    </>
  );
};

export default FoldingRequirementFilterFields;
